import { Material } from '@babylonjs/core/Materials/material';
import { MultiMaterial } from '@babylonjs/core/Materials/multiMaterial';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Axis, Color3, Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths';
import { CreateCylinderVertexData } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreateSphereVertexData } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { SubMesh } from '@babylonjs/core/Meshes/subMesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Scene } from '@babylonjs/core/scene';
import { BaseConnector, Connector, IKbVectorLike, Kb3dObject, KbVector, SketchNode, instanceOf } from '@models/classes';
import { eAxis, eConnectorPositionMode } from '@models/classes/enums';
import { SpaceNode } from '@models/classes/meshes/space-node';
import { KbColor } from '@models/classes/primitives/kb-color';
import { IBoundingBox } from '@models/classes/primitives/primitive-utilities';
import { Token } from '@models/classes/token';
import { ITrackedClass, isTrackedClass } from '@models/interfaces/tracked-class';
import { BoundingBoxRelativeHelper } from '@view/helpers/bounding-box-relative-helper';
import { getRotationBtwVectors, toRadians } from '@view/helpers/math-helper';
import { KbViewer } from '@view/kb-viewer';
import { Subscription } from 'rxjs';
import { Renderer } from '../renderer';
import { RendererManager } from '../renderer-manager';

export interface ConnectorParts {
    mesh: AbstractMesh;
    handleContainer?: TransformNode;
    handleBar?: LinesMesh;
    directionHandle1?: AbstractMesh;
    directionHandle2?: AbstractMesh;
}

export const CONNECTOR_DIAM = 0.1;

export interface IConnectorLayer {
    template: Mesh | null;
    map: Map<string, AbstractMesh>;
    scene: Scene;
    withArrows: boolean;
    visibility: number;
    pickable: boolean;
    parts: Map<string, ConnectorParts>;
}

export class ConnectorRenderer<T extends BaseConnector = Connector> extends Renderer<T, AbstractMesh> {
    protected layers = new Map<'real' | 'utility', IConnectorLayer>();
    protected subscriptionMap = new Map<T, Subscription>();

    protected readonly controlPointScaleRatio: number = 0.24;

    constructor(viewer: KbViewer, scene: Scene, protected rendererManager: RendererManager) {
        super(viewer, scene);

        this.layers.set('real', {
            template: null,
            map: this.babylonMap,
            scene,
            withArrows: !!viewer.options.editorMode,
            visibility: 1.0,
            pickable: !!viewer.options.editorMode,
            parts: new Map(),
        });
        this.layers.set('utility', {
            template: null,
            map: new Map<string, AbstractMesh>(),
            scene: viewer.utilityLayer.utilityLayerScene,
            withArrows: !!viewer.options.editorMode,
            visibility: 0.3,
            pickable: false, // so utility layer doesn't steal the clicks
            parts: new Map(),
        });
    }
    modelToken = Token.Connector;
    protected connectorMaterial: Material;
    protected selectedMaterial: Material;

    public handlesNode(node: Kb3dObject): boolean {
        return instanceOf(node, Token.Connector);
    }

    update3dInternal(c: ITrackedClass<T>, forceScreenScale = false) {
        /**
         * enabled, but not visible
         * enabled and visible
         * not enabled
         */
        const mesh = this.getMesh(c, this.layers.get('real')!);

        const realLayer = this.layers.get('real')!;
        const utilityLayer = this.layers.get('utility')!;
        const utilityMesh = this.getMesh(c, this.layers.get('utility')!);
        const realParts = realLayer.parts.get(c._dynamicId);
        const utilityParts = utilityLayer.parts.get(c._dynamicId);

        if (this.viewer.options.editorMode) {
            this.viewer.camera.maintainNodeScreenSize(mesh, dist => {
                const scaledSize = dist * this.controlPointScaleRatio;
                realParts?.mesh.scaling.set(scaledSize, scaledSize, scaledSize);
                realParts?.handleContainer?.scaling.set(1 / scaledSize, 1 / scaledSize, 1 / scaledSize);
                realParts?.directionHandle1?.scaling.set(scaledSize, scaledSize, scaledSize);
                realParts?.directionHandle2?.scaling.set(scaledSize, scaledSize, scaledSize);
                utilityParts?.mesh.scaling.set(scaledSize, scaledSize, scaledSize);
                utilityParts?.handleContainer?.scaling.set(1 / scaledSize, 1 / scaledSize, 1 / scaledSize);
                utilityParts?.directionHandle1?.scaling.set(scaledSize, scaledSize, scaledSize);
                utilityParts?.directionHandle2?.scaling.set(scaledSize, scaledSize, scaledSize);
            });
        }

        const changes = this.getChanges(c) as Map<Extract<keyof BaseConnector, string>, any>;
        if (changes.has('_isPickable')) {
            mesh.isPickable = c._isPickable;
        }

        if (changes.has('positionZeroToOne') || changes.has('_sketchControlPoints')) {
            if (c.positionMode === eConnectorPositionMode.moveAlongSketch) {
                if (c instanceof Connector) {
                    //this.updatePositionMoveAlongSketch(c);
                    this.updatePosition(c);
                    this.updateDirection(c);
                }
            }
        }

        if (changes.has('position') || changes.has('positionMode')) {
            const bb = c._parent instanceof SpaceNode ? this.viewer.bbCache.get(c._parent) : undefined;
            this.updatePosition(c, bb);
            /**
             * subscribe to bounding box changes of the parent to know that
             * we need to invalidate the position of the connector if applicable
             */
            if (changes.has('positionMode')) {
                if (c.positionMode == eConnectorPositionMode.boundingBox) {
                    this.addSubscription(c);
                } else {
                    this.removeSubscription(c);
                }
            }
        }

        if (changes.hasAny('direction', 'angle')) {
            this.updateDirection(c);
            this.updatePosition(c);
        }

        if (changes.hasAny('enabled', '_visible')) {
            if (c.enabled && c._visible) {
                mesh.setEnabled(true);
                utilityMesh.setEnabled(true);
            } else {
                mesh.setEnabled(false);
                utilityMesh.setEnabled(false);
            }
        }

        if (changes.has('_selected')) {
            if (c._selected) {
                mesh.material = this.getSelectedMaterial(this.layers.get('real')!.withArrows);
            } else {
                mesh.material = this.getConnectorMaterial(this.layers.get('real')!.withArrows);
            }
            utilityMesh.material = mesh.material;
        }

        return { mesh, utilityMesh };
    }

    protected addSubscription(c: ITrackedClass<T>) {
        if (c._parent instanceof SpaceNode) {
            const mesh = c._parent;

            if (!this.subscriptionMap.has(c)) {
                this.subscriptionMap.set(
                    c,
                    this.viewer.bbCache.watchForChanges(mesh).subscribe(bbInfo => {
                        c.setPropertyIsModified('position', true); // Trigger changes on mates and everything using that connector
                        this.updatePosition(c, bbInfo);
                        this.updateScale(c);
                    })
                );
            }
        }
    }

    protected removeSubscription(c: T) {
        const s = this.subscriptionMap.get(c);
        if (s) {
            s.unsubscribe();
            this.subscriptionMap.delete(c);
        }
    }

    public updatePosition(c: T, bb?: IBoundingBox) {
        const mesh = this.getMesh(c, this.layers.get('real')!);
        const utilityMesh = this.getMesh(c, this.layers.get('utility')!);
        if (c.positionMode == eConnectorPositionMode.moveAlongSketch) {
            if (c instanceof Connector) {
                const newPosition = this.updatePositionMoveAlongSketch(c);
                if (newPosition) {
                    mesh.position = utilityMesh.position = newPosition;
                }
            }
        } else if (c.positionMode == eConnectorPositionMode.absolute) {
            mesh.position = utilityMesh.position = c.position.toVec3();
        } else if (c.positionMode == eConnectorPositionMode.boundingBox) {
            if (!bb) {
                bb = c._parent instanceof SpaceNode ? this.viewer.bbCache.get(c._parent) : undefined;
            }
            if (bb) {
                mesh.position = BoundingBoxRelativeHelper.convertRelativeToAbsolute(c, bb);
            } else {
                mesh.position = new Vector3(c.position.x, c.position.y, c.position.z);
            }
            utilityMesh.position = mesh.position;
        }
        if (isTrackedClass(c)) {
            this.rendererManager.referenceProcessor.triggerChangesOnReferencers(c);
        }
    }

    public updateDirection(c: T) {
        const mesh = this.getMesh(c, this.layers.get('real')!);
        const utilityMesh = this.getMesh(c, this.layers.get('utility')!);
        if (c.positionMode === eConnectorPositionMode.moveAlongSketch) {
            if (c instanceof Connector) {
                let newDirection = this.updateDirectionMoveAlongSketch(c);
                if (newDirection) {
                    newDirection = newDirection.normalize();

                    if (c._parent instanceof SketchNode) {
                        const normal = c._parent.normal;
                        const normalVector = new Vector3(1, 0, 0);
                        if (normal === eAxis.y) {
                            normalVector.set(0, 1, 0);
                        } else if (normal === eAxis.z) {
                            normalVector.set(0, 0, 1);
                        }
                        if (mesh.parent instanceof TransformNode) {
                            const connectorDirection = newDirection;
                            const angleRot = Quaternion.RotationAxis(connectorDirection, toRadians(c.angle));
                            const angleRotMatrix = new Matrix();
                            angleRot.toRotationMatrix(angleRotMatrix);
                            const parentDirection = Vector3.TransformCoordinates(normalVector, angleRotMatrix);
                            const crossProduct = Vector3.Cross(connectorDirection, parentDirection);
                            // prettier-ignore
                            const matrix = Matrix.FromArray([connectorDirection.x, connectorDirection.y, connectorDirection.z, 0,
                                                            parentDirection.x, parentDirection.y, parentDirection.z, 0,
                                                            crossProduct.x, crossProduct.y, crossProduct.z, 0,
                                                            0, 0, 0, 1,
                            ]);
                            const newQuaternion = Quaternion.FromRotationMatrix(matrix);
                            mesh.rotationQuaternion = utilityMesh.rotationQuaternion = newQuaternion;
                        }
                    }
                }
            }
        } else {
            /**
             * treat the positive x axis as the direction vector,
             * and the angle around that axis defined by the y axis.
             *
             * See Dragger.connect for a more in depth explanation
             */
            const deltaBtwXaxisAndDirection = getRotationBtwVectors(Axis.X, c.direction.toVec3().normalize());
            const angleRot = Quaternion.RotationAxis(Axis.X, toRadians(c.angle));
            mesh.rotationQuaternion = utilityMesh.rotationQuaternion = deltaBtwXaxisAndDirection.multiply(angleRot);
        }
        this.updateScale(c);
    }

    /**
     * connector meshes are children of their parent mesh, meaning they will scale
     * with any of their ancestor meshes or groups.
     * To counteract this we inverse their world scale.
     * WARNING! this technique does not work well when an ancestor has a non-uniform scale
     * @param c
     */
    public updateScale(c: T) {
        if (this.layers.get('real')!.map.get(c._dynamicId)) {
            //this could get called at any time, make sure the mesh already exists
            const mesh = this.getMesh(c, this.layers.get('real')!);
            const utilityMesh = this.getMesh(c, this.layers.get('utility')!);

            mesh.scaling = Vector3.One();
            mesh.computeWorldMatrix(true);

            if (mesh.parent instanceof TransformNode) {
                const worldScale = mesh.parent.absoluteScaling;

                const numerator = (this.viewer.sceneNode.connectorSize || CONNECTOR_DIAM) / CONNECTOR_DIAM;
                const transformMatrix = Matrix.Scaling(
                    numerator / worldScale.x,
                    numerator / worldScale.y,
                    numerator / worldScale.z
                );
                mesh.setPreTransformMatrix(transformMatrix);
                utilityMesh.setPreTransformMatrix(transformMatrix);

                if (isTrackedClass(c)) {
                    this.rendererManager.referenceProcessor.triggerChangesOnReferencers(c);
                }
            }
        }
    }

    protected updatePositionMoveAlongSketch(connector: Connector) {
        const node = connector._parent;
        const result = new Vector3(0, 0, 0);
        if (node instanceof SketchNode) {
            const sketchLength = node.getSketchLength();
            const posZeroToOne = connector.positionZeroToOne;
            let trackedLength = 0;
            let targetNotFound = true;
            node.paths.forEach(path => {
                let lastPosition: IKbVectorLike | null = null;
                let lastLength = 0;
                path._points.forEach(point => {
                    if (!lastPosition) {
                        lastPosition = point;
                    } else {
                        const currentPos = point;
                        const distance = Math.sqrt(
                            Math.pow(lastPosition.x - currentPos.x, 2) +
                                Math.pow(lastPosition.y - currentPos.y, 2) +
                                Math.pow(lastPosition.z - currentPos.z, 2)
                        );
                        lastLength = trackedLength;
                        trackedLength += distance;
                        const distanceBetweenPoints = trackedLength - lastLength;
                        const distanceFromP1toTarget = posZeroToOne * sketchLength - lastLength;
                        const percentageBetween2Points = distanceFromP1toTarget / distanceBetweenPoints;
                        const vectorBetween2Points = new KbVector(
                            currentPos.x - lastPosition.x,
                            currentPos.y - lastPosition.y,
                            currentPos.z - lastPosition.z
                        );
                        if (trackedLength > posZeroToOne * sketchLength && targetNotFound) {
                            const calculatedPosition = vectorBetween2Points
                                .scale(percentageBetween2Points)
                                .add(lastPosition);
                            result.set(calculatedPosition.x, calculatedPosition.y, calculatedPosition.z);
                            targetNotFound = false;
                        }
                        lastPosition = currentPos;
                    }
                });
                if (posZeroToOne === 1) {
                    const path = node.paths[node.paths.length - 1];
                    const controlPoint = path._points[path._points.length - 1];
                    result.set(controlPoint.x, controlPoint.y, controlPoint.z);
                }
            });
        }
        return result;
    }

    protected updateDirectionMoveAlongSketch(connector: Connector) {
        const node = connector._parent;
        const result = new Vector3(0, 0, 0);
        if (node instanceof SketchNode) {
            const sketchLength = node.getSketchLength();
            const posZeroToOne = connector.positionZeroToOne;
            let trackedLength = 0;

            let targetNotFound = true;
            let lastPathVector: KbVector | null = null;
            let nextPathVector: KbVector | null = null;
            node.paths.forEach(path => {
                let lastPosition: IKbVectorLike | null = null;
                let lastLength = 0;
                path._points.forEach(point => {
                    if (!lastPosition) {
                        lastPosition = point;
                    } else {
                        const currentPos = point;
                        if (lastPosition) {
                            const index = path._points.indexOf(lastPosition) + 1;
                            const index2 = index + 1;
                            if (index !== path._points.length) {
                                const nextPosition = path._points[index];
                                if (index2 !== path._points.length) {
                                    const nextNextPosition = path._points[index2];
                                    nextPathVector = new KbVector(
                                        nextNextPosition.x - nextPosition.x,
                                        nextNextPosition.y - nextPosition.y,
                                        nextNextPosition.z - nextPosition.z
                                    ).normalize();
                                }
                            }
                        }
                        const distance = Math.sqrt(
                            Math.pow(lastPosition.x - currentPos.x, 2) +
                                Math.pow(lastPosition.y - currentPos.y, 2) +
                                Math.pow(lastPosition.z - currentPos.z, 2)
                        );
                        lastLength = trackedLength;
                        trackedLength += distance;
                        const distanceBetweenPoints = trackedLength - lastLength;
                        const distanceFromP1toTarget = posZeroToOne * sketchLength - lastLength;
                        const percentageBetween2Points = distanceFromP1toTarget / distanceBetweenPoints;
                        const vectorBetween2Points = new KbVector(
                            currentPos.x - lastPosition.x,
                            currentPos.y - lastPosition.y,
                            currentPos.z - lastPosition.z
                        );
                        if (trackedLength > posZeroToOne * sketchLength && targetNotFound) {
                            let calculatedDirection = null;
                            if (!connector.interpolateDirection) {
                                calculatedDirection = vectorBetween2Points.normalize();
                            } else {
                                if (!lastPathVector) {
                                    if (nextPathVector) {
                                        calculatedDirection = vectorBetween2Points
                                            .normalize()
                                            .scale(1 - percentageBetween2Points)
                                            .add(
                                                nextPathVector
                                                    .normalize()
                                                    .add(vectorBetween2Points.normalize())
                                                    .normalize()
                                                    .scale(percentageBetween2Points)
                                            )
                                            .normalize();
                                    } else {
                                        calculatedDirection = vectorBetween2Points.normalize();
                                    }
                                } else if (!nextPathVector) {
                                    calculatedDirection = lastPathVector
                                        .scale(1 - percentageBetween2Points)
                                        .add(vectorBetween2Points.scale(1 + percentageBetween2Points))
                                        .scale(1 / 2)
                                        .normalize();
                                } else {
                                    if (percentageBetween2Points <= 0.5) {
                                        calculatedDirection = lastPathVector
                                            .normalize()
                                            .add(vectorBetween2Points.normalize())
                                            .normalize()
                                            .scale(1 - percentageBetween2Points * 2)
                                            .add(vectorBetween2Points.scale(percentageBetween2Points * 2))
                                            .normalize();
                                    } else {
                                        calculatedDirection = vectorBetween2Points
                                            .normalize()
                                            .scale(1 - (percentageBetween2Points - 0.5) * 2)
                                            .add(
                                                nextPathVector
                                                    .normalize()
                                                    .add(vectorBetween2Points.normalize())
                                                    .normalize()
                                                    .scale((percentageBetween2Points - 0.5) * 2)
                                            )
                                            .normalize();
                                    }
                                }
                            }
                            targetNotFound = false;
                            result.set(calculatedDirection.x, calculatedDirection.y, calculatedDirection.z);
                        }
                        lastPathVector = vectorBetween2Points;
                        lastPosition = currentPos;
                    }
                });
            });
            if (posZeroToOne === 1) {
                if (posZeroToOne === 1) {
                    const path = node.paths[node.paths.length - 1];
                    const currentPos = path._points[path._points.length - 1];
                    const lastPosition = path._points[path._points.length - 2];
                    const vectorBetween2Points = new KbVector(
                        currentPos.x - lastPosition.x,
                        currentPos.y - lastPosition.y,
                        currentPos.z - lastPosition.z
                    ).normalize();
                    result.set(vectorBetween2Points.x, vectorBetween2Points.y, vectorBetween2Points.z);
                }
            }
        }
        return result;
    }

    protected getMesh(c: T, layer: IConnectorLayer) {
        let mesh = layer.map.get(c._dynamicId);
        if (!mesh) {
            if (!layer.template) {
                layer.template = this.createMeshTemplate(layer);
            }

            mesh = layer.template.clone(c._dynamicId); // layer.template.createInstance(c._dynamicId);
            mesh.id = c._dynamicId;

            const parentNode = c._parent;

            if (parentNode && parentNode instanceof SpaceNode) {
                const parent3d = this.viewer.getRendererNode(parentNode);
                if (parent3d) {
                    mesh.setParent(parent3d);
                } else {
                    console.warn(`mesh not found for ${parentNode.name}`);
                }
            }

            mesh.isPickable = layer.pickable;
            mesh.position = Vector3.Zero();
            mesh.rotationQuaternion = Quaternion.Identity();
            layer.map.set(c._dynamicId, mesh);
            layer.parts.set(c._dynamicId, {
                mesh,
            });
        }
        return mesh;
    }

    protected createMeshTemplate(layer: IConnectorLayer) {
        const ball = CreateSphereVertexData({
            segments: 8,
            diameter: CONNECTOR_DIAM,
        });

        if (layer.withArrows) {
            const arrowHead = CreateCylinderVertexData({
                diameterTop: 0,
                height: CONNECTOR_DIAM / 2,
                diameterBottom: CONNECTOR_DIAM / 4,
                tessellation: 6,
            });
            arrowHead.transform(
                Matrix.Compose(
                    Vector3.One(),
                    Quaternion.FromEulerAngles(Math.PI / 2, 0, 0),
                    new Vector3(0, 0, CONNECTOR_DIAM * 1.75)
                )
            );

            const arrowShaft = CreateCylinderVertexData({
                diameterTop: CONNECTOR_DIAM / 10,
                height: CONNECTOR_DIAM,
                diameterBottom: CONNECTOR_DIAM / 10,
                tessellation: 6,
            });

            arrowShaft.transform(
                Matrix.Compose(
                    Vector3.One(),
                    Quaternion.FromEulerAngles(Math.PI / 2, 0, 0),
                    new Vector3(0, 0, CONNECTOR_DIAM)
                )
            );

            ball.merge(arrowHead).merge(arrowShaft);
        }

        const mesh = new Mesh('', layer.scene);
        ball.applyToMesh(mesh, false); //updateable false

        if (layer.withArrows) {
            //let ballHalf1 = new SubMesh(0, 0, 231, 0, 600, mesh);
            new SubMesh(1, 0, 231, 0, 600, mesh); // ball half 2
        }

        mesh.rotation.y = Math.PI / 2;
        mesh.bakeCurrentTransformIntoVertices();
        mesh.position = Vector3.Zero();
        mesh.material = this.getConnectorMaterial(layer.withArrows);
        mesh.visibility = layer.visibility;
        mesh.setEnabled(false);
        return mesh;
    }

    public delete3d(c: ITrackedClass<T>) {
        const utilityLayer = this.layers.get('utility')!;
        const utilityMesh = utilityLayer.map.get(c._dynamicId);
        if (utilityMesh) {
            utilityMesh.dispose();
            utilityLayer.map.delete(c._dynamicId);
        }

        this.removeSubscription(c);
        super.delete3d(c);
    }

    protected getConnectorMaterial(twoTone: boolean) {
        if (!this.connectorMaterial) {
            if (twoTone) {
                const m1 = new StandardMaterial('connectorMaterial0', this.scene);
                m1.emissiveColor = KbColor.KbmaxOrange().toColor3();
                m1.disableLighting = true;

                const m2 = new StandardMaterial('connectorMaterial1', this.scene);
                m2.emissiveColor = new Color3(0, 101 / 255, 1); //new Color3(226/255, 101/255, 29/255);// KbColor.KbmaxBlue().toColor3();
                m2.disableLighting = true;

                const m = new MultiMaterial('connectorMaterial', this.scene);
                m.subMaterials.push(m1, m2);
                this.connectorMaterial = m;
            } else {
                const m = new StandardMaterial('connectorMaterial', this.scene);
                m.emissiveColor = this.viewer.highlightColor.toColor3();
                m.disableLighting = true;
                this.connectorMaterial = m;
            }
        }
        return this.connectorMaterial;
    }

    protected getSelectedMaterial(withArrows: boolean) {
        if (!this.selectedMaterial) {
            this.selectedMaterial = this.getConnectorMaterial(withArrows);
            if (withArrows) {
                const m1 = new StandardMaterial('connectorSelectedMaterial0', this.scene);
                m1.emissiveColor = new Color3(254 / 255, 233 / 255, 7 / 255);
                m1.disableLighting = true;

                const m2 = new StandardMaterial('connectorSelectedMaterial1', this.scene);
                m2.emissiveColor = new Color3(2 / 255, 251 / 255, 248 / 255);
                m2.disableLighting = true;

                const m = new MultiMaterial('connectorSelectedMaterial', this.scene);
                m.subMaterials.push(m1, m2);
                this.selectedMaterial = m;
            } else {
                const m = new StandardMaterial('connectorSelectedMaterial', this.scene);
                m.emissiveColor = this.viewer.options.highlightColor!.toColor3();
                m.disableLighting = true;
                this.selectedMaterial = m;
            }
        }
        return this.selectedMaterial;
    }

    public dispose() {
        super.dispose();

        for (const [, layer] of this.layers) {
            if (layer.template) {
                layer.template.dispose();
            }
            layer.map.clear();
        }
        if (this.connectorMaterial) {
            this.connectorMaterial.dispose();
        }
    }
}
